import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Injectable} from '@angular/core';
import {MmCompanyDependencyService, NotificationSenderEnum} from './mm-company-dependency.service';
import {FaultField} from '../fault-field';
import {DynamicFormReadyEvent, FilterSelectFieldConfig, SelectItem} from '@mydocma/dynamic-form';
import {BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subscription} from 'rxjs';
import {map} from 'rxjs/operators';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {getCompanyInfo} from '@docma-mm-core/company/company';
import {TranslateService} from '@ngx-translate/core';
import {FaultDependencyEnum} from './fault-dependency-enum';


@Injectable()
@UntilDestroy()
export class MmCraftService {

  isNotificationSuspended: boolean = false;
  removeClearButton?: boolean = undefined;

  craftField: FaultField;
  craftFieldConfig :FilterSelectFieldConfig;
  formReadyEvent: DynamicFormReadyEvent;

  crafts = new Observable<SelectItem[]>();
  allCompanies: SelectItem[];
  allCrafts: SelectItem[];
  allOrders: SelectItem[];

  changedSubscriptions: Subscription[] = [];

  craftInfoMessageSubject = new BehaviorSubject<FaultDependencyEnum[]>(null);
  craftInfoMessage$ = this.craftInfoMessageSubject.asObservable();

  constructor(private readonly companyDependencyService: MmCompanyDependencyService,
              private readonly translateService: TranslateService) {

    this.translateService.onLangChange
      .pipe(untilDestroyed(this))
      .subscribe(lang => {
        this.craftInfoMessageSubject.next(this.craftInfoMessageSubject.value);
      });
  }

  preInitialize() {
    if (this.changedSubscriptions && (this.changedSubscriptions?.length ?? 0) > 0) {
      this.changedSubscriptions?.forEach(s => {
        s.unsubscribe();
      });
      this.changedSubscriptions = [];
    }
  }

  async initialize(craftField: FaultField, craftFieldConfig :FilterSelectFieldConfig, formReadyEvent: DynamicFormReadyEvent) {
    this.craftField = craftField;
    this.craftFieldConfig = craftFieldConfig;
    this.formReadyEvent = formReadyEvent;

    const result = await firstValueFrom(combineLatest([this.companyDependencyService.allCompanies,
                                              this.companyDependencyService.allOrders,
                                              this.companyDependencyService.allCrafts,
                                              this.companyDependencyService.selectedCompany$,
                                              this.companyDependencyService.selectedOrder$,
                                              this.companyDependencyService.selectedCraft$,
                                              this.companyDependencyService.loadRecentlyUsedEntities(PersonalizationKeysEnum.RecentlyUsedCrafts)])
      .pipe(untilDestroyed(this),
        map(async ([allCompanies, allOrders, allCrafts, selectedCompany, selectedOrder, selectedCraft]) => {
          this.allCompanies = allCompanies;
          this.allCrafts = allCrafts;
          this.allOrders = allOrders;

          const result = await this.processDependencies(allCompanies, allOrders, allCrafts, selectedOrder, selectedCompany, this.companyDependencyService.recentlyUsedCrafts, NotificationSenderEnum.initialize);
          return result;
        })
      ));

    this.crafts = of(result.crafts);
    await this.updateFieldConfig();

    this.changedSubscriptions.push(this.companyDependencyService.selectedCraft$
      .pipe(untilDestroyed(this))
      .subscribe(async param => {
        if (!this.isNotificationSuspended && param.sender === NotificationSenderEnum.craft) {
          await this.setInfoMessage(param, false);
        }
      }));

    this.changedSubscriptions.push(this.companyDependencyService.selectedCompany$
      .pipe(untilDestroyed(this))
      .subscribe(async param => {
        if (!this.isNotificationSuspended && param.sender !== NotificationSenderEnum.initialize && craftFieldConfig) {
          const selectedCompany = param;
          const selectedOrder = await firstValueFrom(this.companyDependencyService.selectedOrder$);
          const result = await this.processDependencies(this.allCompanies, this.allOrders, this.allCrafts, selectedOrder, selectedCompany, this.companyDependencyService.recentlyUsedCrafts, param.sender);
          this.crafts = of(result.crafts);
          await this.updateFieldConfig();

          const currentCraft = this.formReadyEvent.dynamicFormService.getFieldValue(craftFieldConfig.name);

          if (param.sender !== NotificationSenderEnum.craft && result.selectCraft && result.selectCraft.id !== currentCraft && !currentCraft && selectedCompany.value) {
            this.formReadyEvent.dynamicFormService.updateFieldValue(craftFieldConfig.name, result.selectCraft.id, false);
            this.companyDependencyService.selectedCraftSubject.next({
              sender: param.sender,
              value: result.selectCraft
            });
            this.companyDependencyService.onFieldValueChanged.emit();
          }

          const onlyReset = (param.sender === NotificationSenderEnum.order || param.sender === NotificationSenderEnum.company);
          await this.setInfoMessage(param, onlyReset);
        }
      }));

    this.changedSubscriptions.push(this.companyDependencyService.selectedOrder$
      .pipe(untilDestroyed(this))
      .subscribe(async param => {
        if (!this.isNotificationSuspended && param.sender !== NotificationSenderEnum.initialize && craftFieldConfig) {
          const selectedOrder = param;
          const selectedCompany = await firstValueFrom(this.companyDependencyService.selectedCompany$);
          const result = await this.processDependencies(this.allCompanies, this.allOrders, this.allCrafts, selectedOrder, selectedCompany, this.companyDependencyService.recentlyUsedCrafts, param.sender);
          this.crafts = of(result.crafts);
          await this.updateFieldConfig();

          const currentCraft = this.formReadyEvent.dynamicFormService.getFieldValue(craftFieldConfig.name);

          if (param.sender !== NotificationSenderEnum.craft && result.selectCraft && result.selectCraft.id !== currentCraft && !currentCraft && selectedOrder.value) {
            this.formReadyEvent.dynamicFormService.updateFieldValue(craftFieldConfig.name, result.selectCraft.id, false);
            this.companyDependencyService.selectedCraftSubject.next({
              sender: param.sender,
              value: result.selectCraft
            });
            this.companyDependencyService.onFieldValueChanged.emit();
          }
          const onlyReset = (param.sender === NotificationSenderEnum.order || param.sender === NotificationSenderEnum.company);
          await this.setInfoMessage(param, onlyReset);
        }
      }));
  }

  async updateFieldConfig() {
    if (this.craftFieldConfig) {
      this.craftFieldConfig.groupedSource = true;
      this.craftFieldConfig.removeClearButton = (this.removeClearButton ? this.removeClearButton : this.craftFieldConfig.readonly);
      this.craftFieldConfig.source = await firstValueFrom(this.crafts);
      this.formReadyEvent.dynamicFormService.updateFieldConfig(this.craftFieldConfig);
    }
  }

  async processDependencies(allCompanies: SelectItem[], allOrders: SelectItem[], allCrafts: SelectItem[], selectedOrder: {value: any, sender: NotificationSenderEnum}, selectedCompany: {value: any, sender: NotificationSenderEnum}, recentlyUsedCrafts: { [index: number]: number }, sender: NotificationSenderEnum) {
    const groupItems: {[id: number]: SelectItem} = [];
    let selectCraft: SelectItem;

    const order = (selectedOrder?.value?.id ? allOrders.find(x => x.id === selectedOrder.value.id) : null);
    const company = (selectedCompany?.value?.id ? allCompanies.find(x => x.id === selectedCompany.value.id) : null);

    if (order && company) {
      if (order.data.Company.Id === company.id) {
        // FILTERED + LAST USED + ALL
        selectCraft = this.addOrderCompanyCrafts(allCrafts, order, company, groupItems);
        this.companyDependencyService.addRecentlyUsedEntities(allCrafts, recentlyUsedCrafts, groupItems);
        this.addAllCrafts(allCrafts, allOrders, groupItems, order, company);
      }
      else {
        // LAST USED + ALL
        this.companyDependencyService.addRecentlyUsedEntities(allCrafts, recentlyUsedCrafts, groupItems);
        selectCraft = this.addAllCrafts(allCrafts, allOrders, groupItems, order, company, true);
      }
    }
    else if (order && !company) {
      // FILTERED + LAST USED + ALL
      selectCraft = this.addOrderCrafts(allCrafts, order, groupItems);
      this.companyDependencyService.addRecentlyUsedEntities(allCrafts, recentlyUsedCrafts, groupItems);
      this.addAllCrafts(allCrafts, allOrders, groupItems, order, company);
    }
    else if (!order && company) {
      selectCraft = this.addCompanyCrafts(allCrafts, allOrders, company, groupItems);
      this.companyDependencyService.addRecentlyUsedEntities(allCrafts, recentlyUsedCrafts, groupItems);
      this.addAllCrafts(allCrafts, allOrders, groupItems, order, company);
    }
    else {
      // LAST USED + ALL
      this.companyDependencyService.addRecentlyUsedEntities(allCrafts, recentlyUsedCrafts, groupItems);
      selectCraft = this.addAllCrafts(allCrafts, allOrders, groupItems, order, company, true);
    }

    const items = (groupItems && Object.keys(groupItems).length > 0 ? Object.values(groupItems) : allCrafts);
    return {crafts: items, selectCraft: selectCraft};
  }

  addOrderCrafts(allCrafts: SelectItem[], order: SelectItem, groupItems: {[id: number]: SelectItem}) {
    const items: SelectItem[] = allCrafts.filter(x => order.data?.Craft && x.id === order.data.Craft.Id);

    if ((items?.length ?? 0) > 0) {
      const nameFunc = () => this.translateService.instant('MM.fault.dependencyForGoup1').replace('ENTITY', order.text);
      groupItems[0] = {id: 0, text: nameFunc(), data: nameFunc};
      groupItems[0].items = items;
    }

    if ((items?.length ?? 0) === 1) {
      return items[0];
    }
    return null;
  }

  addOrderCompanyCrafts(allCrafts: SelectItem[], order: SelectItem, company: SelectItem , groupItems: {[id: number]: SelectItem}) {
    const items: SelectItem[] = allCrafts.filter(x => x.id === order.data.Craft?.Id);

    if ((items?.length ?? 0) > 0) {
      const nameFunc = () => this.translateService.instant('MM.fault.dependencyForGoup2').replace('ENTITY', order.text).replace('ENTITY2', company.text);
      groupItems[0] = {id: 1, text: nameFunc(), data: nameFunc};
      groupItems[0].items = items;
    }

    if ((items?.length ?? 0) === 1) {
      return items[0];
    }
    return null;
  }

  addCompanyCrafts(allCrafts: SelectItem[], allOrders: SelectItem[], company: SelectItem, groupItems: {[id: number]: SelectItem}) {
    const items = allOrders.filter(o => o.data?.Craft && o.data.Company.Id === company.id)
      .map(c => ({id: c.data.Craft.Id, text: (c.data.Craft.Name1 + (c.data.Craft.Name2 ? ' - ' + c.data.Craft.Name2 : ''))}))
      .reduce((arr: SelectItem[], item: SelectItem) =>  arr.find(p => p.id === item.id ) ? arr : [...arr, item], []);

    if ((items?.length ?? 0) > 0) {  // IF ORDERS: FILTERED + LAST USED + ALL
      const nameFunc = () => this.translateService.instant('MM.fault.dependencyForGoup1').replace('ENTITY', company.text);
      groupItems[0] = {id: 0, text: nameFunc(), data: nameFunc};
      groupItems[0].items = items;
    }

    if ((items?.length ?? 0) === 1) {
      return items[0];
    }
    return null;
  }

  addAllCrafts(allCrafts: SelectItem[], allOrders: SelectItem[], groupItems: {[id: number]: SelectItem}, order: SelectItem, company: SelectItem, returnSelectedCraft: boolean = false) {
    let selectedItem: SelectItem;

    if (returnSelectedCraft) {
      if (order?.data?.Craft?.Id) {
        selectedItem = allCrafts.find(x => x.id === order.data.Company.Id);
      }
      else if (company) {
        const itemsByCompany = allOrders.filter(o => o.data?.Craft && o.data.Company.Id === company.id)
          .map(c => ({
            id: c.data.Company.Id,
            text: (c.data.Company.ShortName + (c.data.Company.Name1 ? ' - ' + c.data.Company.Name1 : '')) + getCompanyInfo(c.data.Company, this.translateService)
          }))
          .reduce((arr: SelectItem[], item: SelectItem) => arr.find(p => p.id === item.id) ? arr : [...arr, item], []);

        if ((itemsByCompany?.length ?? 0) === 1) {
          selectedItem = itemsByCompany[0];
        }
      }
    }


    if (groupItems && Object.keys(groupItems).length > 0) {
      const nameFunc = () => this.translateService.instant('MM.fault.dependencyAllGoup');
      groupItems[2] = {id: 2, text: nameFunc(), data: nameFunc};
      groupItems[2].items = allCrafts;
    }

    if (returnSelectedCraft) {
      return selectedItem;
    }
  }

  async setInfoMessage(param: {value: SelectItem, sender: NotificationSenderEnum}, onlyReset: boolean) {
    if (param.sender !== NotificationSenderEnum.initialize) {
      let infoMessage: FaultDependencyEnum[] = null;

      const selectedCompany = await firstValueFrom(this.companyDependencyService.selectedCompany$);
      const selectedCraft = await firstValueFrom(this.companyDependencyService.selectedCraft$);
      const selectedOrder = await firstValueFrom(this.companyDependencyService.selectedOrder$);

      const company = (selectedCompany?.value?.id ? this.allCompanies.find(x => x.id === selectedCompany.value.id) : null);
      const order = (selectedOrder?.value?.id ? this.allOrders.find(x => x.id === selectedOrder.value.id) : null);

      const craft = (selectedCraft?.value?.id ? this.allCrafts.find(x => x.id === selectedCraft.value.id) : null);

      if (order && company) {
        if (order.data?.Company?.Id === company.id) {
          if (craft && order.data?.Craft?.Id !== craft?.id) {
            // console.warn('CRAFT.MESSAGE #1');
            infoMessage = [FaultDependencyEnum.Order, FaultDependencyEnum.Company];
          }
        }
        else if (order.data?.Company && order.data.Company.Id !== company.id) {
          // console.warn('CRAFT.MESSAGE #2');
          infoMessage = [FaultDependencyEnum.Order];
        }
      }
      else if (order && !company) {
        if (craft && order.data?.Craft?.Id !== craft?.id) {
          // console.warn('CRAFT.MESSAGE #3');
          infoMessage = [FaultDependencyEnum.Order];
        }
      }
      else if (!order && company) {
        const items = this.allOrders.filter(o => o.data?.Craft && o.data?.Company.Id === company.id)
          .map(c => ({id: c.data.Craft.Id, text: (c.data?.Craft.Name1 + (c.data?.Craft.Name2 ? ' - ' + c.data?.Craft.Name2 : '')), data: c.data}))
          .reduce((arr: SelectItem[], item: SelectItem) =>  arr.find(p => p.id === item.id ) ? arr : [...arr, item], []);

        if (craft && !items.some(x => x.data?.Craft?.id === craft?.id)) {
          // console.warn('CRAFT.MESSAGE #1');
          infoMessage = [FaultDependencyEnum.Company];
        }
      }
      else {
        // NO MESSAGE!
      }

      if (!onlyReset || (onlyReset && !infoMessage)) {
        // console.warn('CRAFT.MESSAGE ' + infoMessage);
        this.craftInfoMessageSubject.next(infoMessage);
      }
    }
  }

  async reloadCrafts() {
    const selectedCompany = await firstValueFrom(this.companyDependencyService.selectedCompany$);
    const selectedOrder = await firstValueFrom(this.companyDependencyService.selectedOrder$);
    const result = await this.processDependencies(this.allCompanies, this.allOrders, this.allCrafts, selectedOrder, selectedCompany, this.companyDependencyService.recentlyUsedCrafts, NotificationSenderEnum.initialize);
    this.crafts = of(result.crafts);
    await this.updateFieldConfig();
  }
}
